import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  FormControlLabel,
  Switch,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import GroupsIcon from "@mui/icons-material/Groups";
import SettingsIcon from "@mui/icons-material/Settings";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import DashboardLayout from "../../layouts/DashboardLayout";
import type { ManufacturerRep } from "../../types/DB2";
import ManufacturerRepsDataProvider, {
  useManufacturerRepsData,
} from "../../context/ManufacturerRepsDataProvider";
import { useUserData } from "../../context/UserDataFetcher";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  query,
  where,
} from "firebase/firestore";

const STATUS_OPTIONS = ["active", "pending", "suspended"] as const;

type FormState = {
  name: string;
  legalName?: string;
  contactEmail: string;
  contactPhone?: string;
  contactAddress?: string;
  manufacturerIdsText?: string;
  territoryNotes?: string;
  principalEmail?: string;
  adminEmailsText?: string;
  status: typeof STATUS_OPTIONS[number];
  isDemo: boolean;
};

const defaultForm: FormState = {
  name: "",
  contactEmail: "",
  status: "active",
  isDemo: false,
  principalEmail: "",
  adminEmailsText: "",
};

const commaList = (value?: string[]) => (value && value.length ? value.join(", ") : "");

const parseList = (value?: string) =>
  value
    ? Array.from(
        new Set(
          value
            .split(/[\n,]+/)
            .map((entry) => entry.trim())
            .filter(Boolean)
        )
      )
    : [];

const RepModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onSave: (payload: FormState) => Promise<void> | void;
  rep?: ManufacturerRep | null;
  allowManufacturerIdInput: boolean;
}> = ({ open, onClose, onSave, rep, allowManufacturerIdInput }) => {
  const [form, setForm] = useState<FormState>(defaultForm);
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});
  const db = getFirestore();

  const loadUserEmail = async (uid: string | undefined | null) => {
    if (!uid) return "";
    try {
      const snap = await getDoc(doc(db, "users", uid));
      if (snap.exists()) {
        const data = snap.data() as any;
        return data.email || "";
      }
    } catch (err) {
      console.warn("[ManageManufacturerReps] failed to load user email", { uid, err });
    }
    return "";
  };

  React.useEffect(() => {
    let cancelled = false;
    const hydrate = async () => {
      if (rep) {
        const principalEmail = await loadUserEmail(rep.principalUserId);
        const adminEmails = await Promise.all(
          (rep.adminUserIds ?? []).map((uid) => loadUserEmail(uid))
        );
        if (cancelled) return;
        setForm({
          name: rep.name || "",
          legalName: rep.legalName || "",
          contactEmail: rep.contactInfo?.email || "",
          contactPhone: rep.contactInfo?.phone || "",
          contactAddress: rep.contactInfo?.address || "",
          manufacturerIdsText: commaList(rep.manufacturerIds),
          territoryNotes: rep.territoryNotes || "",
          principalEmail: principalEmail || "",
          adminEmailsText: adminEmails.filter(Boolean).join(", "),
          status: (rep.status as FormState["status"]) || "active",
          isDemo: rep.isDemo ?? false,
        });
      } else {
        setForm(defaultForm);
      }
    };
    hydrate();
    return () => {
      cancelled = true;
    };
  }, [rep, open]);

  const updateField = (key: keyof FormState, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSave = async () => {
    const nextErrors: typeof errors = {};
    if (!form.name.trim()) nextErrors.name = "Name is required";
    if (!form.contactEmail.trim()) nextErrors.email = "Contact email is required";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    await onSave(form);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{rep ? "Edit Representative" : "Add Representative"}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
        <TextField
          label="Organization Name"
          value={form.name}
          onChange={(e) => updateField("name", e.target.value)}
          required
          error={!!errors.name}
          helperText={errors.name}
        />
        <TextField
          label="Legal Name"
          value={form.legalName ?? ""}
          onChange={(e) => updateField("legalName", e.target.value)}
        />
        <TextField
          label="Contact Email"
          value={form.contactEmail}
          onChange={(e) => updateField("contactEmail", e.target.value)}
          required
          error={!!errors.email}
          helperText={errors.email}
        />
        <TextField
          label="Contact Phone"
          value={form.contactPhone ?? ""}
          onChange={(e) => updateField("contactPhone", e.target.value)}
        />
        <TextField
          label="Contact Address"
          value={form.contactAddress ?? ""}
          onChange={(e) => updateField("contactAddress", e.target.value)}
        />
        {allowManufacturerIdInput && (
          <TextField
            label="Manufacturer IDs (comma separated)"
            value={form.manufacturerIdsText ?? ""}
            onChange={(e) => updateField("manufacturerIdsText", e.target.value)}
            helperText="Developers only: specify one or more manufacturer IDs."
          />
        )}
        <TextField
          label="Territory Notes"
          value={form.territoryNotes ?? ""}
          onChange={(e) => updateField("territoryNotes", e.target.value)}
          multiline
          minRows={2}
        />
        <TextField
          label="Principal User Email"
          value={form.principalEmail ?? ""}
          onChange={(e) => updateField("principalEmail", e.target.value)}
        />
        <TextField
          label="Admin User Emails (comma separated)"
          value={form.adminEmailsText ?? ""}
          onChange={(e) => updateField("adminEmailsText", e.target.value)}
        />
        <TextField
          label="Status"
          select
          value={form.status}
          onChange={(e) => updateField("status", e.target.value)}
        >
          {STATUS_OPTIONS.map((status) => (
            <MenuItem key={status} value={status}>
              {status}
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          control={<Switch checked={form.isDemo} onChange={(e) => updateField("isDemo", e.target.checked)} />}
          label="Demo organization"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          {rep ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ManufacturerRepsManagerScreen: React.FC<{ embedded?: boolean }> = ({ embedded }) => {
  const { reps, createRep, updateRep, deleteRep } = useManufacturerRepsData();
  const userData = useUserData();
  const db = getFirestore();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRep, setEditingRep] = useState<ManufacturerRep | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRep, setMenuRep] = useState<ManufacturerRep | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const openAdd = () => {
    setEditingRep(null);
    setModalOpen(true);
  };

  const openEdit = (rep: ManufacturerRep) => {
    setEditingRep(rep);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingRep(null);
  };

  const openMenu = (rep: ManufacturerRep, event: React.MouseEvent<HTMLElement>) => {
    setMenuRep(rep);
    setMenuAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuRep(null);
  };

  const totalDealerCount = (rep: ManufacturerRep) => {
    const summary = rep.dealerScopeSummary || {};
    return Object.values(summary).reduce((sum, entry) => sum + (entry?.dealerCount ?? 0), 0);
  };

  const columns = useMemo(
    () => [
      {
        header: "Organization",
        accessorKey: "name",
      },
      {
        header: "Contact Email",
        accessorFn: (row: ManufacturerRep) => row.contactInfo?.email || "",
      },
      {
        header: "Manufacturers",
        accessorFn: (row: ManufacturerRep) => row.manufacturerIds?.length || 0,
      },
      {
        header: "Dealers",
        accessorFn: (row: ManufacturerRep) => totalDealerCount(row),
      },
      {
        header: "Status",
        accessorKey: "status",
      },
      {
        header: "Demo?",
        accessorFn: (row: ManufacturerRep) => (row.isDemo ? "Yes" : "No"),
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    data: reps ?? [],
    columns,
    enableColumnFilters: false,
    enableColumnActions: false,
    enableColumnOrdering: false,
    enableHiding: false,
    enableRowSelection: false,
    enableRowActions: true,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enablePagination: true,
    positionActionsColumn: "last",
    initialState: {
      pagination: { pageIndex: 0, pageSize: 100 },
    },
    renderTopToolbarCustomActions: () => (
      <IconButton color="primary" onClick={openAdd}>
        <AddIcon />
      </IconButton>
    ),
    renderRowActions: ({ row }) => (
      <IconButton size="small" onClick={(e) => openMenu(row.original as ManufacturerRep, e)}>
        <MoreVertIcon />
      </IconButton>
    ),
    enableExpanding: true,
    renderDetailPanel: ({ row }) => {
      const rep = row.original as ManufacturerRep;
      return (
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Details
          </Typography>
          <Typography variant="body2">
            Manufacturers: {rep.manufacturerIds?.join(", ") || "None"}
          </Typography>
          <Typography variant="body2">
            Territory: {rep.territoryNotes || "Not specified"}
          </Typography>
          <Typography variant="body2">
            Principal: {rep.principalUserId || "Not assigned"}
          </Typography>
          <Typography variant="body2">
            Admin Users: {rep.adminUserIds?.join(", ") || "None"}
          </Typography>
        </Box>
      );
    },
    muiTopToolbarProps: {
      sx: (theme) => ({
        display: "flex",
        alignItems: "center",
        backgroundColor: theme.palette.background.paper,
        "& > div": {
          flexDirection: "row-reverse",
        },
      }),
    },
  });

  const canEditManufacturerAssignments = userData?.role === "developer" || userData?.isDev === true;
  const fallbackManufacturerId = userData?.manufacturerId?.trim();

  const resolveUserIdByEmail = async (rawEmail: string): Promise<string | null> => {
    const email = rawEmail.trim();
    if (!email) return null;
    const usersRef = collection(db, "users");
    const emailLower = email.toLowerCase();
    const variants: any[][] = [
      [where("email", "==", email)],
      [where("emailLower", "==", emailLower)],
    ];
    if (fallbackManufacturerId) {
      variants.forEach((filters) => filters.push(where("manufacturerId", "==", fallbackManufacturerId)));
    }
    for (const filters of variants) {
      try {
        const snap = await getDocs(query(usersRef, ...(filters as any)));
        if (!snap.empty) {
          return snap.docs[0].id;
        }
      } catch (err) {
        console.warn("[ManageManufacturerReps] user lookup failed", { email, filters, err });
      }
    }
    return null;
  };

  const handleSave = async (form: FormState) => {
    const payload: Partial<ManufacturerRep> = {
      name: form.name.trim(),
      legalName: form.legalName?.trim() || undefined,
      contactInfo: {
        email: form.contactEmail.trim(),
        ...(form.contactPhone?.trim() ? { phone: form.contactPhone.trim() } : {}),
        ...(form.contactAddress?.trim() ? { address: form.contactAddress.trim() } : {}),
      },
      territoryNotes: form.territoryNotes?.trim() || undefined,
      status: form.status,
      isDemo: form.isDemo,
    };

    const principalEmail = form.principalEmail?.trim() ?? "";
    if (principalEmail.length) {
      const resolvedPrincipal = await resolveUserIdByEmail(principalEmail);
      if (!resolvedPrincipal) {
        window.alert(`Could not find a user with email ${principalEmail}. Create the user first, then try again.`);
        return;
      }
      payload.principalUserId = resolvedPrincipal;
    }

    const adminEmails = parseList(form.adminEmailsText);
    if (form.adminEmailsText !== undefined) {
      if (adminEmails.length) {
        const resolvedAdminIds: string[] = [];
        for (const email of adminEmails) {
          const uid = await resolveUserIdByEmail(email);
          if (!uid) {
            window.alert(`Could not find a user with email ${email}. All admin emails must belong to existing users.`);
            return;
          }
          resolvedAdminIds.push(uid);
        }
        payload.adminUserIds = resolvedAdminIds;
      } else if (editingRep?.adminUserIds?.length) {
        payload.adminUserIds = [];
      }
    }

    const manualIds = canEditManufacturerAssignments ? parseList(form.manufacturerIdsText) : [];
    const existingIds = editingRep?.manufacturerIds ?? [];
    const combined = new Set<string>([...existingIds, ...manualIds]);
    if (fallbackManufacturerId) combined.add(fallbackManufacturerId);
    const nextManufacturerIds = Array.from(combined).filter(Boolean);
    if (!nextManufacturerIds.length) {
      window.alert(
        "Unable to save representative because no manufacturer ID is available. Please ensure your user record has a manufacturerId or use a developer account to specify IDs manually."
      );
      return;
    }

    payload.manufacturerIds = nextManufacturerIds;

    Object.keys(payload).forEach((key) => {
      const value = (payload as any)[key];
      if (value === undefined) {
        delete (payload as any)[key];
      }
    });

    if (editingRep?.id) {
      await updateRep(editingRep.id, payload);
    } else {
      await createRep(payload);
    }
    closeModal();
  };

  const handleDelete = async () => {
    if (menuRep?.id) {
      await deleteRep(menuRep.id);
    }
    setDeleteConfirm(false);
    closeMenu();
  };

  const canAccess =
    userData?.role === "developer" ||
    userData?.isDev === true ||
    (userData?.role === "manufacturer" && userData?.isAdmin);

  const content = (
    <>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <GroupsIcon color="primary" sx={{ fontSize: "2.5rem" }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Manufacturer Representatives
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Developer preview - manage rep organizations and their scopes.
            </Typography>
          </Box>
        </Box>
        <IconButton color="primary" sx={{ alignSelf: "flex-start" }}>
          <SettingsIcon />
        </IconButton>
      </Box>
      <MaterialReactTable table={table} />

      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            if (menuRep) openEdit(menuRep);
            closeMenu();
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteConfirm(true);
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)}>
        <DialogTitle>Delete Representative</DialogTitle>
        <DialogContent>
          <Typography>
            Delete
            {" "}
            <strong>{menuRep?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <RepModal
        open={modalOpen}
        onClose={closeModal}
        onSave={handleSave}
        rep={editingRep ?? undefined}
        allowManufacturerIdInput={canEditManufacturerAssignments}
      />
    </>
  );

  if (!canAccess) {
    const locked = (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5">Developer access required</Typography>
        <Typography variant="body2" color="text.secondary">
          This preview is currently limited to developer accounts.
        </Typography>
      </Box>
    );
    return embedded ? locked : <DashboardLayout>{locked}</DashboardLayout>;
  }

  return embedded ? content : <DashboardLayout>{content}</DashboardLayout>;
};

const ManageManufacturerReps: React.FC = () => (
  <DashboardLayout>
    <Box sx={{ p: 4, textAlign: "center" }}>
      <Typography variant="h4" sx={{ fontWeight: 600 }}>
        Manufacturer Reps
      </Typography>
      <Typography variant="body1" sx={{ mt: 2 }}>
        This feature is currently disabled for demos.
      </Typography>
    </Box>
  </DashboardLayout>
);

export const ManufacturerRepsManagerPanel: React.FC = () => (
  <ManufacturerRepsDataProvider>
    <ManufacturerRepsManagerScreen embedded />
  </ManufacturerRepsDataProvider>
);

export default ManageManufacturerReps;
